// src/ui/settings/store.ts — client-only settings persistence (UI-06 / D-04).
//
// A plain module (NO zustand — none is used anywhere in this repo; the stack
// table lists it but `grep -rln zustand src/` is empty, see 07-PATTERNS.md
// correction). Five settings persist as a single JSON blob under the reserved
// `hunter:*` localStorage namespace (PROJECT.md). Every storage access uses the
// fail-open try/catch idiom from src/ui/overlays/InstallHint.tsx — a private-mode
// / quota / malformed-blob failure never throws; the in-memory value still
// applies. Volume gains are clamped to [0,1] on load (T-07-06 tampering).

export type JoystickSize = 'small' | 'medium' | 'large';

export interface Settings {
  /** Global master volume 0..1 (default 1.0). */
  master: number;
  /** Music bus volume 0..1 (default 0.7). */
  music: number;
  /** SFX bus volume 0..1 (default 1.0). */
  sfx: number;
  /** Virtual joystick size bucket (default 'medium'). */
  joystickSize: JoystickSize;
  /** Haptics on catch / foxguard (default true; inert where unsupported). */
  vibration: boolean;
}

// 07-UI-SPEC §Surface 2 defaults.
export const DEFAULT_SETTINGS: Settings = {
  master: 1.0,
  music: 0.7,
  sfx: 1.0,
  joystickSize: 'medium',
  vibration: true,
};

export const SETTINGS_KEY = 'hunter:settings';

/** Joystick size bucket → nipplejs px (Joystick.tsx hardcodes 96 today). */
const JOYSTICK_PX: Record<JoystickSize, number> = {
  small: 96,
  medium: 120,
  large: 144,
};

export function joystickSizePx(size: JoystickSize): number {
  return JOYSTICK_PX[size];
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.min(1, Math.max(0, n));
}

const VALID_SIZES: readonly JoystickSize[] = ['small', 'medium', 'large'];

/**
 * Read `hunter:settings`, merge over DEFAULT_SETTINGS, clamp the gains, and
 * fail open to defaults on any read/parse error (InstallHint idiom).
 */
export function loadSettings(): Settings {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(SETTINGS_KEY);
  } catch {
    // localStorage disabled (private mode / SSR / jsdom) — fail open.
    return { ...DEFAULT_SETTINGS };
  }
  if (raw === null) return { ...DEFAULT_SETTINGS };

  let parsed: Partial<Settings>;
  try {
    parsed = JSON.parse(raw) as Partial<Settings>;
  } catch {
    // Malformed blob (tampering / truncation) — fail open.
    return { ...DEFAULT_SETTINGS };
  }
  if (parsed === null || typeof parsed !== 'object') {
    return { ...DEFAULT_SETTINGS };
  }

  const merged: Settings = { ...DEFAULT_SETTINGS };
  if (typeof parsed.master === 'number') merged.master = clamp01(parsed.master);
  if (typeof parsed.music === 'number') merged.music = clamp01(parsed.music);
  if (typeof parsed.sfx === 'number') merged.sfx = clamp01(parsed.sfx);
  if (
    typeof parsed.joystickSize === 'string' &&
    VALID_SIZES.includes(parsed.joystickSize)
  ) {
    merged.joystickSize = parsed.joystickSize;
  }
  if (typeof parsed.vibration === 'boolean') merged.vibration = parsed.vibration;
  return merged;
}

// Module-level in-memory truth, seeded from storage at import time.
let current: Settings = loadSettings();

type Listener = (settings: Settings) => void;
const listeners = new Set<Listener>();

export function getSettings(): Settings {
  return current;
}

/**
 * Merge a partial patch into `current`, persist (fail-open), and notify
 * subscribers — even if the persist write throws, so the in-memory value and
 * any live UI stay consistent.
 */
export function saveSettings(patch: Partial<Settings>): void {
  const next: Settings = { ...current, ...patch };
  if (typeof patch.master === 'number') next.master = clamp01(patch.master);
  if (typeof patch.music === 'number') next.music = clamp01(patch.music);
  if (typeof patch.sfx === 'number') next.sfx = clamp01(patch.sfx);
  current = next;
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(current));
  } catch {
    // Quota / private mode — silent; in-memory value still applies.
  }
  for (const fn of [...listeners]) fn(current);
}

/** Subscribe to settings changes; returns an unsubscribe fn. */
export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  return (): void => {
    listeners.delete(fn);
  };
}
